import { compareTableName } from "@/components/mapping/auto-tag-progress/constants";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { useCorrectRollForwardTable } from "@/hooks/mutations/use-roll-forward-info";
import { useWorkbookId } from "@/hooks/use-workbook";
import { cn } from "@/lib/utils";
import queries from "@/queries";
import { popModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { RadioButton } from "@/toggle-ui-kit/control";
import { ScrollbarArea } from "@/toggle-ui-kit/scrollbar";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import {
  ForwardModalDescription,
  ForwardModalFooter,
  ForwardModalHeader,
} from "./forward-modal-chrome";
import { TableHtmlPreview } from "./table-html-preview";

/** 표 개수 배지 — 2단계 상태 칩(Figma 5611:341515)과 같은 모양. */
const CountChip = ({ children }: { children: React.ReactNode }) => (
  <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-label-disable px-1 py-0.5 caption-1-medium text-label-alternative">
    {children}
  </span>
);

/** 현재 매칭 표에 붙는 배지. */
const CurrentChip = () => (
  <span className="flex h-5 shrink-0 items-center justify-center rounded-full bg-primary-base px-1 py-0.5 caption-1-medium text-static-white">
    현재
  </span>
);

/**
 * 표별 롤포워드 매칭 수정 모달 — src 워크북의 표를 카테고리 아코디언으로 보여주고,
 * 고른 표로 target 표의 이월 짝(overwrite/tag 동일 src)을 교정한다.
 *
 * 레이아웃은 상위 "XBRL 태그 불러오기" 모달과 같은 골격을 쓴다 — 헤더(46) · 설명(40) ·
 * 스크롤 본문 · 푸터(56), 회색 요약 카드 → 붙어 있는 안내 줄 → 44px 행.
 * 표를 눈으로 비교해야 해서 폭만 넓힌다(800px).
 */
export const SelectSourceTableModal = ({
  targetTableId,
  targetTableName,
  sourceWorkbookId,
  currentSourceTableId,
}: {
  targetTableId: string;
  targetTableName: string;
  sourceWorkbookId: string;
  currentSourceTableId: string | null;
}) => {
  const { data: workbookId } = useWorkbookId();
  const { data: categoriesData, isLoading } = useQuery({
    ...queries.workbook(sourceWorkbookId)._ctx.categories(),
    enabled: !!sourceWorkbookId,
  });
  const { mutate: correct, isPending } = useCorrectRollForwardTable();
  const [selectedId, setSelectedId] = useState<string | null>(
    currentSourceTableId,
  );

  // 카테고리 이름은 "6. 현금..." 처럼 번호에 점이 붙어 compareTableName 이 못 읽는다
  // — 선두 숫자만 뽑아 숫자 비교, 번호 없으면 뒤로.
  const categoryNumber = (name: string) => {
    const match = name.match(/^(\d+)/);
    return match ? Number.parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
  };

  const categories = useMemo(
    () =>
      (categoriesData?.categories ?? [])
        .filter((c) => c.tables.length > 0)
        .map((c) => ({
          ...c,
          tables: [...c.tables].sort((a, b) =>
            compareTableName(a.name, b.name),
          ),
        }))
        .sort((a, b) => {
          const na = categoryNumber(a.category);
          const nb = categoryNumber(b.category);
          if (na !== nb) return na - nb;
          return a.category.localeCompare(b.category, "ko", { numeric: true });
        }),
    [categoriesData],
  );

  // 현재 매칭된 표가 속한 카테고리를 기본으로 펼친다.
  const defaultCategoryId = useMemo(() => {
    if (!currentSourceTableId) return categories[0]?.id;
    return (
      categories.find((c) =>
        c.tables.some((t) => t.id === currentSourceTableId),
      )?.id ?? categories[0]?.id
    );
  }, [categories, currentSourceTableId]);

  // undefined = 아직 손대지 않음 → 현재 매칭이 있는 카테고리를 펼쳐 둔다.
  const [expandedId, setExpandedId] = useState<string | null | undefined>(
    undefined,
  );
  const openCategoryId =
    expandedId === undefined ? defaultCategoryId : expandedId;

  const tableNameById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => c.tables.forEach((t) => map.set(t.id, t.name)));
    return map;
  }, [categories]);

  const currentSourceTableName = currentSourceTableId
    ? tableNameById.get(currentSourceTableId)
    : undefined;
  const selectedTableName = selectedId
    ? tableNameById.get(selectedId)
    : undefined;

  return (
    <ModalWrapper className="flex h-[800px] max-h-[calc(100vh-48px)] w-[800px] max-w-[calc(100vw-48px)] flex-col gap-0 overflow-hidden rounded-[8px] border-0 bg-background-base p-0 shadow-xxlarge ring-1 ring-inset ring-line-normal">
      <ForwardModalHeader title="매칭 표 수정" />
      <ForwardModalDescription>
        이 표에 불러올 이전 보고서의 표를 선택하세요.
      </ForwardModalDescription>

      <ScrollbarArea
        className="min-h-0 w-full flex-1"
        viewportClassName="[&>div]:!block"
      >
        <div className="flex flex-col px-6">
          {/* 현재 표 요약(+ 안내 줄)은 상단에 고정한다 — 아래 후보 표들을 스크롤하며
              눈으로 비교하는 화면이라, 비교 기준이 스크롤과 함께 사라지면 안 된다.
              둘을 한 덩어리로 묶어야 top-0 이 겹치지 않는다. pb-2 는 이 블록과 아래
              목록 사이 간격(예전 부모 gap-2 자리). */}
          <div className="sticky top-0 z-10 flex flex-col gap-2 bg-background-base pb-2">
            {/* 현재 표 요약 — 상위 모달의 보고서 요약 카드와 같은 톤 */}
            <div className="flex w-full shrink-0 flex-col gap-2 rounded-[8px] bg-background-gray-alternative p-4">
              <div className="flex items-center gap-2">
                <span className="shrink-0 caption-1-regular text-label-alternative">
                  현재 표
                </span>
                <span
                  className="label-1-normal-medium min-w-0 flex-1 truncate text-label-normal"
                  title={targetTableName}
                >
                  {targetTableName}
                </span>
              </div>
              {workbookId && (
                <TableHtmlPreview
                  workbookId={workbookId}
                  tableId={targetTableId}
                  scaleFactor={0.7}
                  className="max-h-[200px] rounded-[6px] bg-static-white p-2 ring-1 ring-inset ring-line-normal"
                />
              )}
              <div className="flex items-center gap-2">
                <span className="shrink-0 caption-1-regular text-label-alternative">
                  현재 매칭
                </span>
                <span
                  className="caption-1-medium min-w-0 flex-1 truncate text-label-normal"
                  title={currentSourceTableName}
                >
                  {currentSourceTableName ?? "매칭 없음"}
                </span>
              </div>
            </div>

            {!isLoading && categories.length > 0 && (
              <div className="flex h-5 items-center justify-between gap-2">
                <span className="shrink-0 caption-1-regular text-label-alternative">
                  불러올 표 선택
                </span>
                <span
                  className="min-w-0 truncate caption-1-regular text-label-alternative"
                  title={selectedTableName}
                >
                  {selectedTableName ?? "선택 안 됨"}
                </span>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 caption-1-regular text-label-alternative">
              <Loader2 className="size-4 animate-spin" />
              표를 불러오는 중입니다...
            </div>
          ) : categories.length > 0 ? (
            <div className="flex flex-col gap-1">
              {categories.map((category) => {
                const expanded = openCategoryId === category.id;
                return (
                  <div
                    key={category.id}
                    className="rounded-[8px] bg-static-white ring-1 ring-inset ring-line-normal"
                  >
                    <div
                      className="flex cursor-pointer select-none items-center gap-2 p-3"
                      onClick={() =>
                        setExpandedId(expanded ? null : category.id)
                      }
                    >
                      <ChevronRight
                        strokeWidth={1.8}
                        className={cn(
                          "size-5 shrink-0 text-label-normal transition-transform",
                          expanded && "rotate-90",
                        )}
                      />
                      <span
                        className="label-1-normal-medium min-w-0 flex-1 truncate text-label-normal"
                        title={category.category}
                      >
                        {category.category}
                      </span>
                      <CountChip>{category.tables.length}</CountChip>
                    </div>

                    {expanded && (
                      <div className="grid grid-cols-2 gap-2 border-t border-line-normal p-3">
                        {category.tables.map((table) => {
                          const selected = selectedId === table.id;
                          return (
                            <button
                              key={table.id}
                              type="button"
                              onClick={() => setSelectedId(table.id)}
                              className={cn(
                                "flex flex-col gap-2 rounded-[8px] p-2 text-left ring-1 ring-inset transition-colors",
                                selected
                                  ? "bg-blue-50 ring-blue-500"
                                  : "bg-static-white ring-line-normal hover:bg-background-neutral-normal",
                              )}
                            >
                              <span className="flex w-full items-center gap-2">
                                <RadioButton
                                  checked={selected}
                                  size={16}
                                  tabIndex={-1}
                                  className="pointer-events-none"
                                  aria-hidden
                                />
                                <span
                                  className="caption-1-medium min-w-0 flex-1 truncate text-label-normal"
                                  title={table.name}
                                >
                                  {table.name}
                                </span>
                                {currentSourceTableId === table.id && (
                                  <CurrentChip />
                                )}
                              </span>
                              <TableHtmlPreview
                                workbookId={sourceWorkbookId}
                                tableId={table.id}
                                mini
                                className="h-36 w-full rounded-[6px] bg-static-white ring-1 ring-inset ring-line-normal"
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-[8px] bg-background-neutral-alternative p-4 caption-1-regular text-label-alternative ring-1 ring-inset ring-line-normal">
              선택할 수 있는 표가 없습니다.
            </div>
          )}
        </div>
      </ScrollbarArea>

      <ForwardModalFooter>
        <Button
          variant="outlined"
          color="secondary"
          size="base"
          onClick={popModal}
        >
          이전
        </Button>
        <Button
          variant="solid"
          color="primary"
          size="base"
          leftIcon={isPending && <Loader2 className="size-4 animate-spin" />}
          disabled={
            !selectedId || selectedId === currentSourceTableId || isPending
          }
          onClick={() =>
            selectedId &&
            correct(
              { tableId: targetTableId, sourceTableId: selectedId },
              { onSuccess: popModal },
            )
          }
        >
          적용
        </Button>
      </ForwardModalFooter>
    </ModalWrapper>
  );
};
